class KokoRitual extends HTMLElement {
  connectedCallback() {
    if (this.observer) return;
    this.classList.add('is-ready', 'is-carousel');
    this.products = [...this.querySelectorAll('.rosayaa-banner__product')];
    this.stage = this.querySelector('.rosayaa-banner__products');
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.index = 0;
    this.visible = false;
    this.busy = false;
    this.animations = [];
    this.events = new AbortController();
    const options = { signal: this.events.signal };
    this.products.forEach((product, index) => this.setVisible(product, index === 0));
    this.stage.addEventListener('focusin', () => this.sync(), options);
    this.stage.addEventListener('focusout', () => queueMicrotask(() => this.sync()), options);
    document.addEventListener('visibilitychange', () => this.sync(), options);
    this.motion.addEventListener('change', () => {
      if (this.motion.matches) this.animations.forEach(animation => animation.finish());
      this.sync();
    }, options);
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].isIntersecting;
      this.sync();
    }, { threshold: 0.15 });
    this.observer.observe(this);
  }

  setVisible(product, visible) {
    product.hidden = !visible;
    product.style.display = visible ? 'block' : 'none';
    product.style.visibility = visible ? 'visible' : 'hidden';
    product.inert = !visible;
    product.setAttribute('aria-hidden', String(!visible));
  }

  sync() {
    clearTimeout(this.timer);
    const stopped = this.motion.matches;
    const resting = stopped || !this.visible || document.hidden || this.stage.contains(document.activeElement);
    this.classList.toggle('is-motion-paused', resting);
    this.animations.forEach(animation => {
      if (animation.playState === 'finished' || animation.playState === 'idle') return;
      if (resting) animation.pause();
      else animation.play();
    });
    if (!stopped && this.visible && !document.hidden && !this.busy &&
        !this.stage.contains(document.activeElement) && this.products.length > 1) {
      this.timer = setTimeout(() => this.next(), 1600);
    }
  }

  async next() {
    if (this.busy || this.products.length < 2) return;
    clearTimeout(this.timer);
    const incomingIndex = (this.index + 1) % this.products.length;
    const incoming = this.products[incomingIndex];
    const outgoing = this.products[this.index];
    const image = incoming.querySelector('img');
    if (!image.complete || !image.naturalWidth) { this.sync(); return; }
    this.busy = true;
    const mobile = matchMedia('(max-width: 1024px)').matches;
    this.classList.toggle('is-transitioning', mobile);
    const distance = (this.stage.clientWidth + outgoing.clientWidth) / 2 + 70;
    const centered = 'translate(-50%, -50%)';
    const entrance = centered + ' translate(' + distance + 'px, 26px) rotate(11deg)';
    incoming.style.transform = this.motion.matches ? centered : entrance;
    this.setVisible(incoming, true);
    outgoing.inert = true;
    if (!this.motion.matches) {
      const contact = incoming.clientWidth * 0.24;
      const timing = { duration: 1100, fill: 'both' };
      this.animations = [
        outgoing.animate([
          { offset: 0, transform: centered },
          { offset: 0.4, transform: centered, easing: 'cubic-bezier(0.42, 0, 0.7, 1)' },
          { offset: 0.74, transform: centered + ' translate(' + (-distance * 0.55) + 'px, -18px) rotate(-12deg)', easing: 'cubic-bezier(0.2, 0, 0.6, 1)' },
          { offset: 1, transform: centered + ' translate(' + (-distance) + 'px, 14px) rotate(-19deg)' }
        ], timing),
        incoming.animate(mobile ? [
          { transform: centered + ' translate(' + distance + 'px, 12px) rotate(6deg)', easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
          { transform: centered }
        ] : [
          { offset: 0, transform: centered + ' translate(' + distance + 'px, 26px) rotate(11deg)', easing: 'cubic-bezier(0.22, 0.7, 0.35, 1)' },
          { offset: 0.4, transform: centered + ' translate(' + contact + 'px, -12px) rotate(-4deg)', easing: 'cubic-bezier(0.3, 0, 0.25, 1)' },
          { offset: 0.78, transform: centered + ' translate(-8px, -5px) rotate(-2deg)', easing: 'cubic-bezier(0.2, 0.7, 0.3, 1)' },
          { offset: 1, transform: centered }
        ], timing)
      ];
      await Promise.all(this.animations.map(animation => animation.finished.catch(() => {})));
    }
    if (!this.isConnected) return;
    this.setVisible(outgoing, false);
    incoming.style.transform = centered;
    this.animations.forEach(animation => animation.cancel());
    outgoing.style.transform = centered;
    this.animations = [];
    this.classList.remove('is-transitioning');
    this.index = incomingIndex;
    this.busy = false;
    this.sync();
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.events?.abort();
    this.observer?.disconnect();
    this.observer = null;
    this.animations?.forEach(animation => animation.cancel());
    this.classList.remove('is-transitioning');
  }
}

if (!customElements.get('koko-ritual')) {
  customElements.define('koko-ritual', KokoRitual);
}
